import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { account } from "../lib/appwrite.js";
import AuthLoadingScreen from "../components/AuthLoadingScreen.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { signInWithEmailPassword, consumeFromPasswordReset } from "../lib/authSession.js";
import { redirectAfterAuth, resolvePostAuthDestination } from "../lib/authRedirect.js";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, postLoginPath, role, refetch, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState(null);
  const [resetError, setResetError] = useState("");

  // If a session is already active, skip the login page — unless the user just reset
  // their password and must sign in again with the new credentials.
  useEffect(() => {
    if (loginLoading || forgotMode) return;
    if (location.state?.fromPasswordReset || consumeFromPasswordReset()) return;
    if (status !== "authenticated" || !postLoginPath || !user) return;
    const dest = resolvePostAuthDestination({
      role,
      postLoginPath,
      fromPathname: location.state?.from?.pathname,
    });
    navigate(dest, { replace: true });
  }, [status, postLoginPath, role, user, location.state, navigate, loginLoading, forgotMode]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      await signInWithEmailPassword(email, password);
      await redirectAfterAuth({
        refetch,
        navigate,
        fromPathname: location.state?.from?.pathname,
      });
    } catch (err) {
      setLoginError(err?.message ?? "Invalid email or password.");
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleSendReset(e) {
    e.preventDefault();
    setResetStatus("loading");
    setResetError("");
    try {
      const resetUrl = `${window.location.origin}/reset-password`;
      await account.createRecovery(resetEmail, resetUrl);
      setResetStatus("success");
    } catch (err) {
      setResetError(err?.message ?? "Something went wrong. Please try again.");
      setResetStatus("error");
    }
  }

  function enterForgotMode() {
    setResetEmail(email);
    setResetStatus(null);
    setResetError("");
    setForgotMode(true);
  }

  const backgrounds = (
    <>
      <div
        className="pointer-events-none absolute left-1/2 top-[40%] size-[320px] -translate-x-[120%] rounded-full bg-hc-warm/15 blur-[64px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-[42%] size-[288px] translate-x-[20%] rounded-full bg-hc-secondary/20 blur-[64px]"
        aria-hidden
      />
    </>
  );

  if (status === "loading" || loginLoading || (status === "authenticated" && !forgotMode)) {
    return <AuthLoadingScreen />;
  }

  if (forgotMode) {
    return (
      <div className="hc-page-shell relative min-h-screen w-full overflow-hidden font-sans">
        {backgrounds}
        <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[384px] flex-col items-center justify-center px-4 py-16">
          <img src="/logo_full.png" alt="Hey Coach AI logo" className="w-full max-w-[384px] h-auto" />

          <div className="w-full max-w-[384px] rounded-[16px] border border-hc-surface-border-strong bg-hc-surface p-7 shadow-[0px_25px_50px_0px_rgba(0,0,0,0.35)]">
            {resetStatus === "success" ? (
              <div className="flex flex-col items-center gap-4 py-2 text-center">
                <div className="flex size-12 items-center justify-center rounded-full bg-hc-primary/15">
                  <i className="icon-check text-xl text-hc-primary leading-none" aria-hidden="true" />
                </div>
                <div>
                  <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">Check your email</h1>
                  <p className="mt-2 text-xs leading-5 text-hc-text-dim">
                    We sent a password reset link to <span className="font-medium text-hc-text">{resetEmail}</span>. It expires in 1 hour.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setForgotMode(false)}
                  className="mt-2 text-xs text-hc-primary hover:underline"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setForgotMode(false)}
                  className="mb-5 flex items-center gap-1.5 text-xs text-hc-text-dim hover:text-hc-text"
                >
                  <i className="icon-arrow-left leading-none" aria-hidden="true" />
                  Back to sign in
                </button>
                <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">Reset your password</h1>
                <p className="mt-2 text-xs leading-4 text-hc-text-dim">
                  Enter your email and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleSendReset} className="mt-8 flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="reset-email" className="text-xs font-medium text-hc-text-muted">
                      Email address
                    </label>
                    <input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] px-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
                    />
                  </div>

                  {resetStatus === "error" && (
                    <p className="rounded-[10px] border border-hc-error/30 bg-hc-error/10 px-3 py-2 text-xs text-hc-error">
                      {resetError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={resetStatus === "loading"}
                    className="relative mt-2 flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] px-4 text-base font-semibold tracking-[-0.31px] text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 disabled:opacity-60"
                  >
                    {resetStatus === "loading" ? "Sending…" : "Send reset link"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hc-page-shell relative min-h-screen w-full overflow-hidden font-sans">
      {backgrounds}

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[384px] flex-col items-center justify-center px-4 py-16">
        <img
          src={'/logo_full.png'}
          alt="Hey Coach AI logo"
          className="w-full max-w-[384px] h-auto"
        />

        <div className="w-full max-w-[384px] rounded-[16px] border border-hc-surface-border-strong bg-hc-surface p-7 shadow-[0px_25px_50px_0px_rgba(0,0,0,0.35)]">
          <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">
            Sign in to your account
          </h1>
          <p className="mt-2 text-xs leading-4 text-hc-text-dim">
            Sign in to view your programs and manage billing.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="email"
                className="text-xs font-medium text-hc-text-muted"
              >
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] px-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor="password"
                  className="text-xs font-medium text-hc-text-muted"
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={enterForgotMode}
                  className="text-xs text-hc-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] py-3 pr-12 pl-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-hc-text-dim"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`${showPassword ? "icon-eye-off" : "icon-eye"} block leading-none`} aria-hidden="true" />
                </button>
              </div>
            </div>

            {loginError && (
              <p className="rounded-[10px] border border-hc-error/30 bg-hc-error/10 px-3 py-2 text-xs text-hc-error">
                {loginError}
              </p>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="relative mt-2 flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] px-4 text-base font-semibold tracking-[-0.31px] text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 disabled:opacity-60"
            >
              {loginLoading ? "Signing in…" : "Sign in"}
              {!loginLoading && <i className="icon-arrow-right" aria-hidden="true" />}
            </button>
          </form>

          <div className="mt-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-hc-surface-border-strong" />
            <span className="text-xs text-hc-text-dim">or</span>
            <div className="h-px flex-1 bg-hc-surface-border-strong" />
          </div>

          <button
            type="button"
            onClick={() => navigate("/register")}
            className="mt-4 flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] border border-hc-surface-border-strong px-4 text-base font-semibold tracking-[-0.31px] text-hc-text transition hover:border-hc-primary/40 hover:text-hc-text"
          >
            Register
          </button>
        </div>
      </div>
    </div>
  );
}
